import { computed, inject } from '@angular/core';
import type { FactoryEdit } from '@indutrust/factory-model';
import {
  withCrudEntitiesEvents,
  withOrderedEntities,
} from '@indutrust/shared-util';
import { signalStore, withComputed } from '@ngrx/signals';
import { adminFactoriesCrudEvents } from './admin-factories.events';
import { FactoryApiClient } from './factory-api-client.service';

/**
 * Signal store managing the platform-wide factory collection from an
 * **admin** point of view.
 *
 * Wraps `${api}/factories` (the same endpoint
 * {@link FactoryApiClient} talks to), but unlike
 * {@link import('../company-factory/+state/company-factories.store').CompanyFactoriesStore}
 * it is **not** scoped to the authenticated user's `company_id` and is
 * **not** auto-loaded on auth — the collection is admin-only and is
 * loaded on demand when the consumer enters an admin surface that needs
 * cross-company visibility (the platform-wide directory at
 * `/backoffice/factories` and the verification queue at
 * `/backoffice/factories/pending`).
 *
 * Exposes:
 * - the full collection via `entities()` / `orderedEntities()` for
 *   future admin views;
 * - `pendingFactories()` — the subset awaiting review, which drives the
 *   pending queue UI and is used by the supplier→admin verification
 *   e2e flow.
 *
 * The collection is filtered client-side because the mocked CRUD GET
 * does not honour query parameters yet; once the real backend grows a
 * `?status=pending` filter, the load handler can pass `params` here
 * without consumers changing.
 *
 * @example
 * const store = inject(AdminFactoriesStore);
 * const dispatch = injectDispatch(adminFactoriesCrudEvents);
 * dispatch.loadAllRequested({});
 * effect(() => console.log(store.pendingFactories()));
 */
export const AdminFactoriesStore = signalStore(
  { providedIn: 'root' },
  withCrudEntitiesEvents<FactoryEdit>(adminFactoriesCrudEvents, () =>
    inject(FactoryApiClient)
  ),
  withOrderedEntities<FactoryEdit>(),
  withComputed(({ entities }) => ({
    pendingFactories: computed(() =>
      entities().filter((factory) => factory.status === 'pending')
    ),
  }))
);
